import React, { useEffect, useRef, useState, ChangeEvent } from 'react'
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import { WizardStepperFooter } from '../components/WizardStepperFooter'
import { setScreensaverURL } from '../features/store/storeSlice'
import { useDispatch, useSelector } from 'react-redux'
import { getScreensaverOptionsUrl } from '../libs/utils'

type ScreensaverOption = {
  value: string
  label: string
  url: string
}

interface Props {
  isNewProduct?: boolean
}

const useStyles = makeStyles(() =>
  createStyles({
    page: {
      display: 'flex',
      flexDirection: 'row',
      gap: '2rem',
      width: '100%',
      padding: '1.5rem 0',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
    },
    section: {
      flex: 1,
      minWidth: '300px',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.25rem',
      alignItems: 'stretch',
    },
    card: {
      background: '#fff',
      borderRadius: '16px',
      padding: '1.5rem',
    },
    select: {
      width: '100%',
    },
    videoWrapper: {
      width: '100%',
      aspectRatio: '9 / 16',
      maxWidth: '270px',
      minHeight: '320px',
      maxHeight: '55vh',
      borderRadius: '16px',
      overflow: 'hidden',
      backgroundColor: '#000',
      backgroundImage: 'url(/assets/screensaver/bgimage.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(8px)',
        zIndex: 1,
        pointerEvents: 'none',
      },
    },

    video: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      position: 'relative',
      zIndex: 2,
    },

    videoText: {
      fontFamily: 'Inter, sans-serif',
      letterSpacing: '1.5px',

      position: 'absolute',
      bottom: '25%',
      zIndex: 3,
      color: '#fff',
      textAlign: 'center',
      pointerEvents: 'none',
    },
  })
)

const EditScreensaverPage: React.FC<Props> = ({ isNewProduct }) => {
  const styles = useStyles()
  const dispatch = useDispatch()
  const videoRef = useRef<HTMLVideoElement | null>(null)

  const [screensaverOptions, setScreensaverOptions] = useState<
    ScreensaverOption[]
  >([])
  const [selectedScreensaver, setSelectedScreensaver] = useState('')
  const [isLoadingOptions, setIsLoadingOptions] = useState(true)

  const { screensaverurl } = useSelector((state: any) => state.vmData.store)

  // Load options
  useEffect(() => {
    const fetchOptions = async () => {
      const url = getScreensaverOptionsUrl()
      if (!url) return setIsLoadingOptions(false)

      try {
        const response = await fetch(url)
        if (!response.ok)
          throw new Error(`Failed to load options: ${response.status}`)
        const options = (await response.json()) as ScreensaverOption[]
        setScreensaverOptions(options)
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoadingOptions(false)
      }
    }

    fetchOptions()
  }, [])

  // Sync video reload
  useEffect(() => {
    videoRef.current?.load()
  }, [selectedScreensaver])

  // Sync selected option with store
  useEffect(() => {
    if (!screensaverOptions.length) return

    const matchingOption = screensaverOptions.find(
      (o) => o.url === screensaverurl
    )
    if (matchingOption) {
      setSelectedScreensaver(matchingOption.url)
      return
    }

    if (
      selectedScreensaver &&
      screensaverOptions.some((o) => o.url === selectedScreensaver)
    ) {
      return
    }

    const fallbackUrl = screensaverOptions[0].url
    setSelectedScreensaver(fallbackUrl)
    dispatch(setScreensaverURL(fallbackUrl))
  }, [screensaverOptions, screensaverurl, selectedScreensaver, dispatch])

  const handleScreensaverChange = (event: ChangeEvent<{ value: unknown }>) => {
    const url = event.target.value as string
    setSelectedScreensaver(url)
    dispatch(setScreensaverURL(url))
  }

  const isStepValid = Boolean(selectedScreensaver)

  return (
    <Box className={styles.page}>
      {/* Selection Section */}
      <Box className={`${styles.section} ${styles.card}`}>
        <Typography variant="h5">Select Screensaver</Typography>
        <FormControl variant="outlined" className={styles.select}>
          <InputLabel id="screensaver-select-label">Screensaver</InputLabel>
          <Select
            labelId="screensaver-select-label"
            id="screensaver-select"
            value={isLoadingOptions ? '' : selectedScreensaver}
            onChange={handleScreensaverChange}
            label="Screensaver"
            disabled={isLoadingOptions}
          >
            {isLoadingOptions ? (
              <MenuItem value="" disabled>
                Loading screensavers...
              </MenuItem>
            ) : (
              screensaverOptions.map((option) => (
                <MenuItem key={option.value} value={option.url}>
                  {option.label}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Box>

      {/* Preview Section */}
      <Box className={`${styles.section} ${styles.card}`}>
        <Typography variant="h5">Preview</Typography>
        <Box className={styles.videoWrapper}>
          <video
            autoPlay
            key={selectedScreensaver}
            ref={videoRef}
            className={styles.video}
            muted
            loop
            playsInline
          >
            <source src={selectedScreensaver} />
            Your browser does not support the video tag.
          </video>
          <Box className={styles.videoText}>
            <Typography variant="h5">
              Try them on, <br /> come a little <br /> closer
            </Typography>
          </Box>
        </Box>
      </Box>

      <WizardStepperFooter isValid={isStepValid} isNewProduct={isNewProduct} />
    </Box>
  )
}

export default EditScreensaverPage
